import React, { useState } from "react"
import { postJson } from "../../helpers/http"
import { API_ENDPOINTS } from "../../helpers/api"

const PasswordInput = ({ value, onChange, placeholder, showPassword, onToggleShow, disabled, autoComplete }) => (
  <div className="auth-password-wrapper">
    <input
      type={showPassword ? "text" : "password"}
      className="input-base auth-input"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      autoComplete={autoComplete}
    />
    <button
      type="button"
      className="auth-password-toggle"
      onClick={onToggleShow}
      tabIndex={-1}
      title={showPassword ? "Hide password" : "Show password"}
    >
      {showPassword ? "Hide" : "Show"}
    </button>
  </div>
)

const AuthForms = ({ onLogin, mode, onModeChange, supportEmail, crossSiteNote = null }) => {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [passwordConfirmation, setPasswordConfirmation] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const toggleShowPassword = () => setShowPassword((s) => !s)

  const isLogin = mode === "login"
  const canSubmit = email.trim() && password && !isSubmitting && (isLogin || password === passwordConfirmation)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!canSubmit) return

    setIsSubmitting(true)
    setError(null)

    const endpoint = isLogin ? API_ENDPOINTS.LOGIN : API_ENDPOINTS.SIGNUP
    const body = isLogin ? { email, password } : { email, password, password_confirmation: passwordConfirmation }

    const { data, error: apiError } = await postJson(endpoint, body)
    setIsSubmitting(false)

    if (apiError) {
      setError(apiError)
      return
    }

    if (data.user) onLogin(data.user)
  }

  const switchMode = () => {
    onModeChange(isLogin ? "signup" : "login")
    setError(null)
    setPassword("")
    setPasswordConfirmation("")
  }

  return (
    <div className="auth-forms-container">
      <div className="section-label">{isLogin ? "Login" : "Sign Up"}</div>
      <form onSubmit={handleSubmit}>
        <input
          type="email"
          className="input-base auth-input"
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isSubmitting}
          autoComplete="email"
        />
        <PasswordInput
          value={password}
          onChange={setPassword}
          placeholder="Password"
          showPassword={showPassword}
          onToggleShow={toggleShowPassword}
          disabled={isSubmitting}
          autoComplete={isLogin ? "current-password" : "new-password"}
        />
        {!isLogin && (
          <PasswordInput
            value={passwordConfirmation}
            onChange={setPasswordConfirmation}
            placeholder="Confirm password"
            showPassword={showPassword}
            onToggleShow={toggleShowPassword}
            disabled={isSubmitting}
            autoComplete="new-password"
          />
        )}
        {error && (
          <div className="error-message">
            {error}
            {isLogin && (
              <div className="password-reset-hint">
                Forgot your password?{" "}
                <span style={{ whiteSpace: "nowrap" }}>
                  Contact <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
                </span>
              </div>
            )}
          </div>
        )}
        <button
          type="submit"
          className="btn-base btn-secondary choices-input-btn choices-input-btn-full"
          disabled={!canSubmit}
          title={isLogin ? "Sign in to your account" : "Create a new account"}
        >
          {isSubmitting ? "..." : isLogin ? "Login" : "Sign Up"}
        </button>
      </form>
      <button
        type="button"
        className="auth-switch-btn"
        onClick={switchMode}
        title={isLogin ? "Switch to sign up form" : "Switch to login form"}
      >
        {isLogin ? "Need an account? Sign up" : "Have an account? Login"}
      </button>
      {crossSiteNote && <div className="auth-cross-site-note">{crossSiteNote}</div>}
    </div>
  )
}

export default AuthForms
